import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Link } from 'react-router-dom';
import PhotoGallery from './PhotoGallery';
import VideoPlayer from './VideoPlayer';
import bandPhotos from './bandPhotos';
import mcsorleysGigFlyer from '../assets/gig_announcements/mcsorleys-october-2.jpg';

const detailRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  color: '#2b211d',
};

function Home() {
  return (
    <Box component="main">
      <Box
        component="section"
        sx={{
          position: 'relative',
          height: { xs: 'calc(100svh - 118px)', md: 'calc(100svh - 132px)' },
          minHeight: { xs: 570, md: 560 },
          maxHeight: 700,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-end',
          bgcolor: '#0b0909',
        }}
      >
        <Box
          aria-hidden="true"
          className="contact-sheet-reveal"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            gridTemplateRows: {
              xs: 'repeat(5, minmax(0, 1fr))',
              md: 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          {bandPhotos.map((photo) => (
            <Box
              key={photo.src}
              component="img"
              src={photo.src}
              alt=""
              width="160"
              height="120"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(1.06) contrast(1.03)',
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: {
              xs: 'linear-gradient(180deg, rgba(11, 9, 9, 0.18) 0%, rgba(11, 9, 9, 0.78) 52%, #0b0909 100%)',
              md: 'linear-gradient(90deg, rgba(11, 9, 9, 0.98) 0%, rgba(11, 9, 9, 0.9) 37%, rgba(11, 9, 9, 0.38) 72%, rgba(11, 9, 9, 0.16) 100%)',
            },
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3, md: 5 },
            pb: { xs: 6, md: 8 },
          }}
        >
          <Box className="hero-reveal" sx={{ maxWidth: 720 }}>
            <Typography
              sx={{
                color: 'secondary.main',
                fontWeight: 700,
                fontSize: { xs: '0.9rem', md: '1rem' },
                mb: 1.5,
              }}
            >
              NJ + PA | Live since 2017
            </Typography>
            <Typography
              component="h1"
              variant="h1"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '4.5rem', sm: '6.25rem', md: '7.5rem' },
                lineHeight: 0.86,
                textWrap: 'balance',
              }}
            >
              Malpractice
            </Typography>
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: { xs: '1.25rem', sm: '1.45rem', md: '1.65rem' },
                lineHeight: 1.45,
                fontWeight: 500,
                maxWidth: 620,
                mt: 2,
                textWrap: 'pretty',
              }}
            >
              A crowd-first showband built for packed dance floors, big singalongs, and nights people remember.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 4, alignItems: { xs: 'stretch', sm: 'center' } }}
            >
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowOutwardIcon />}
                sx={{
                  bgcolor: 'secondary.main',
                  color: '#110c0a',
                  '&:hover': { bgcolor: '#ffd06f' },
                }}
              >
                Book Malpractice
              </Button>
              <Button
                component="a"
                href="#live-video"
                variant="outlined"
                size="large"
                startIcon={<PlayArrowRoundedIcon />}
                sx={{
                  color: 'text.primary',
                  borderColor: 'rgba(248, 241, 231, 0.55)',
                  '&:hover': {
                    borderColor: 'text.primary',
                    bgcolor: 'rgba(248, 241, 231, 0.08)',
                  },
                }}
              >
                Watch us live
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'secondary.main' }}>
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 4 } }}
        >
          <Typography
            variant="h3"
            sx={{
              color: '#1b130f',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.25rem' },
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            Live music. Big crowd energy. No two shows alike.
          </Typography>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{ bgcolor: '#f1e7d8', color: '#1c1511', py: { xs: 8, md: 12 } }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(300px, 0.72fr) minmax(0, 1fr)',
              },
              gap: { xs: 5, md: 10 },
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={mcsorleysGigFlyer}
              alt="Malpractice Band at McSorley's Ale House on October 2 from 7:30 to 11:30 PM"
              loading="lazy"
              sx={{
                display: 'block',
                width: '100%',
                maxWidth: 460,
                mx: { xs: 'auto', md: 0 },
                borderRadius: '2px',
                boxShadow: '18px 18px 0 #e95d3f',
              }}
            />

            <Box>
              <Typography sx={{ color: '#b43a25', fontWeight: 800, mb: 1 }}>
                Next show
              </Typography>
              <Typography
                component="h2"
                variant="h2"
                sx={{
                  color: '#17100d',
                  fontSize: { xs: '3.6rem', sm: '4.75rem', md: '6.25rem' },
                  lineHeight: 0.9,
                  textWrap: 'balance',
                }}
              >
                McSorley's Ale House
              </Typography>
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Box sx={detailRowStyles}>
                  <CalendarMonthOutlinedIcon />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                    Friday, October 2
                  </Typography>
                </Box>
                <Box sx={detailRowStyles}>
                  <ScheduleOutlinedIcon />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                    7:30-11:30 PM
                  </Typography>
                </Box>
                <Box sx={detailRowStyles}>
                  <LocationOnOutlinedIcon />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                    2330 Haverford Avenue, Ardmore
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ mt: 5, alignItems: { xs: 'stretch', sm: 'center' } }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  endIcon={<ArrowOutwardIcon />}
                  sx={{
                    bgcolor: '#17100d',
                    color: '#f8f1e7',
                    '&:hover': { bgcolor: '#3b2921' },
                  }}
                >
                  Book your event
                </Button>
                <Button
                  component="a"
                  href="https://www.google.com/maps/search/?api=1&query=2330+Haverford+Avenue+Ardmore+PA"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  endIcon={<ArrowOutwardIcon />}
                  sx={{
                    color: '#7f281b',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                  }}
                >
                  Get directions
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      <PhotoGallery />

      <Box
        component="section"
        id="live-video"
        sx={{ bgcolor: '#171210', py: { xs: 8, md: 12 } }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'minmax(240px, 0.48fr) minmax(0, 1fr)',
              },
              gap: { xs: 4, md: 7 },
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography sx={{ color: 'secondary.main', fontWeight: 700, mb: 1 }}>
                Watch the set
              </Typography>
              <Typography
                component="h2"
                variant="h2"
                sx={{ fontSize: { xs: '3.4rem', md: '5rem' }, lineHeight: 0.95 }}
              >
                See the room come alive
              </Typography>
            </Box>
            <VideoPlayer />
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: 'primary.main', color: '#140c09' }}>
        <Container
          maxWidth="xl"
          sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 7, md: 9 } }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 4,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '3.75rem', sm: '5rem', md: '6.5rem' },
                lineHeight: 0.9,
                maxWidth: 760,
              }}
            >
              Put a real show on your calendar
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<ArrowOutwardIcon />}
              sx={{
                bgcolor: '#0b0909',
                color: '#f8f1e7',
                flexShrink: 0,
                '&:hover': { bgcolor: '#2c1e19' },
              }}
            >
              Start booking
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
